import React, { useState, useEffect, useLayoutEffect, useRef } from "react";

let globalUnmuted = false;
import { Icons } from "./icons.jsx";
import { LiveDot } from "./primitives.jsx";
import {
  HoverVideoContainer,
  HoverVideoGradientBg,
  HoverVideoGrid,
  HoverVideoGlow,
  HoverVideoShimmer,
  HoverVideoElement,
  HoverVideoOverlay,
  HoverVideoAvatarWrap,
  HoverVideoAvatarRing,
  HoverVideoAvatarImg,
  HoverVideoPlayBadge,
  HoverVideoGuruLabel,
  HoverVideoGuruSubtitle,
  HoverVideoGuruName,
  HoverVideoLoadingBadge,
  HoverVideoPlayingBadge,
  HoverVideoMuteBtn,
} from "./style.js";

// ── HoverVideoArt — smooth PiP ────────────────────────────────────────────────
const HoverVideoArt = ({
  program,
  hovered,
  playToken,
  onAskPlay,
  fillHeight = false,
}) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [isTouch, setIsTouch] = useState(false);
  const [inView, setInView] = useState(false);
  const delayTimer = useRef(null);
  const [muted, setMuted] = useState(true);
  const [accentStart, accentEnd] = program.accent ?? ["#01ECFF", "#962EFF"];

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setDims({ width: el.offsetWidth, height: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(hover: none)");
    const update = () => setIsTouch(mql.matches);
    update();
    mql.addEventListener?.("change", update);
    return () => mql.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isTouch || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio >= 0.6),
      { threshold: [0, 0.6, 1] },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      setInView(false);
    };
  }, [isTouch]);

  const active = isTouch ? inView : hovered;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (active) {
      onAskPlay?.(program.id);
      const delay = isTouch ? 1500 : 200;
      delayTimer.current = setTimeout(() => {
        video.load();
        video.muted = true;
        setShowVideo(true);
        video.play().then(() => {
          if (globalUnmuted) {
            video.muted = false;
            setMuted(false);
          }
        }).catch(() => {});
      }, delay);
    } else {
      clearTimeout(delayTimer.current);
      setShowVideo(false);
      setMuted(true);
      try { video.muted = true; video.pause(); } catch (_) {}
    }
    return () => clearTimeout(delayTimer.current);
  }, [active, isTouch]);

  const toggleMute = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    const next = !muted;
    video.muted = next;
    setMuted(next);
    globalUnmuted = !next;
  };

  const AVATAR = 96,
    PIP_SCALE = 0.45,
    PIP_PAD = 14;
  const isPip = active && showVideo;
  const { width, height } = dims;
  const aLeft =
    width > 0 ? (isPip ? width - PIP_PAD - AVATAR * PIP_SCALE : width / 2 - AVATAR / 2) : 0;
  const aTop =
    height > 0 ? (isPip ? height - PIP_PAD - AVATAR * PIP_SCALE : height / 2 - AVATAR / 2) : 0;
  const aSize = isPip ? AVATAR * PIP_SCALE : AVATAR;

  return (
    <HoverVideoContainer ref={containerRef} $fillHeight={fillHeight}>
      <HoverVideoGradientBg $a={accentStart} $b={accentEnd} />
      <HoverVideoGrid />
      <HoverVideoGlow $a={accentStart} />
      {!active && <HoverVideoShimmer />}
      <HoverVideoElement
        ref={videoRef}
        playsInline
        loop
        muted
        preload="metadata"
        src={program.intro_video ?? "https://gurunow-media-prod.s3.us-east-1.amazonaws.com/media/GN_+Getting+Started+(1).mp4"}
        $showVideo={showVideo}
      />
      <HoverVideoOverlay $showVideo={showVideo} />
      {width > 0 && (
        <HoverVideoAvatarWrap $left={aLeft} $top={aTop} $size={aSize}>
          <HoverVideoAvatarRing $a={accentStart} $b={accentEnd} $isPip={isPip} />
          <HoverVideoAvatarImg
            $src={program.guru?.photo}
            $isPip={isPip}
          />
          {!isPip && (
            <HoverVideoPlayBadge $a={accentStart} $b={accentEnd}>
              <Icons.play size={14} style={{ color: "#fff" }} />
            </HoverVideoPlayBadge>
          )}
        </HoverVideoAvatarWrap>
      )}
      <HoverVideoGuruLabel $hovered={active}>
        <HoverVideoGuruSubtitle>Your Guru</HoverVideoGuruSubtitle>
        <HoverVideoGuruName>
          {program.guru?.full_name?.split(" ")[0]}
        </HoverVideoGuruName>
      </HoverVideoGuruLabel>
      {!showVideo && active && (
        <HoverVideoLoadingBadge>
          <Icons.play size={12} /> <span>Loading intro…</span>
        </HoverVideoLoadingBadge>
      )}
      {showVideo && (
        <HoverVideoMuteBtn $muted={muted} onClick={toggleMute}>
          {muted ? <Icons.volumeOff size={12} /> : <Icons.volume size={12} />}
          <span>{muted ? "Unmute" : "Mute"}</span>
        </HoverVideoMuteBtn>
      )}
      {showVideo && (
        <HoverVideoPlayingBadge>
          <LiveDot />{" "}
          <span>{program.guru?.full_name?.split(" ")[0]}'s Introduction Video</span>
        </HoverVideoPlayingBadge>
      )}
    </HoverVideoContainer>
  );
};

export default HoverVideoArt;
